import logo from "./logo.svg";
import "./App.css";
import { Outlet, RouterProvider, createBrowserRouter, useNavigate } from "react-router-dom";
import React, { Suspense } from "react";
import PageNotFound404 from "./components/PageNotFound404";
import CheckAuth from "./components/common/CheckAuth";
import { store } from './store'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { LS_key_firstName, LS_key_user, getLocalData, shallShowTab } from "./utils/helper";
import Unauthorized from "./components/Unauthorized";
import { MODULE_NON_VOTER, MODULE_REPORTS, MODULE_SUPPORTER, usersRoute, MODULE_USERS, MODULE_VOTER, nonVotersRoute, reportsRoute, supportersRoute, MODULE_BIRTHDAYS, birthdaysRoute, votersRoute,votersCallRoute, worksRoute, MODULE_WORKS, electionPollsRoute, MODULE_ELECTION_POLLS, appointmentsRoute, MODULE_APPOINTMENTS, auditsRoute, MODULE_AUDIT } from "./utils/constants";
import { setReduxState } from "./slices/appSlice";
import { ErrorBoundary } from "react-error-boundary";
import CommonError from "./components/CommonError";
import LanguageSelector from "./components/Translator";
import {Helmet} from "react-helmet";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LoginComponent = React.lazy(() => import("./components/Login"));
const DashboardComponent = React.lazy(() => import("./components/Dashboard"));
const HomepageComponent = React.lazy(() => import("./components/Homepage"));
const VotersComponent = React.lazy(() => import("./components/Voters"));
const NonVotersComponent = React.lazy(() => import("./components/NonVoters"));
const VotersCallComponent = React.lazy(() => import("./components/VotersCall"));
const ReportsComponent = React.lazy(() => import("./components/Reports"));
const SupportersComponent = React.lazy(() => import("./components/Supporters"));
const UsersComponent = React.lazy(() => import("./components/Users"));
const BirthdaysComponent = React.lazy(() => import("./components/Birthdays"));
const WorksComponent = React.lazy(() => import("./components/Works"));
const ElectionPollsComponent = React.lazy(() => import("./components/ElectionPolls"));
const AppointmentsComponent = React.lazy(() => import("./components/Appointments"));
const AuditComponent = React.lazy(() => import("./components/Audit"));

const PrivateRoute = ({ moduleName, children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const { shouldVerifyDetails } = useSelector(state => state.app)

  if (!localStorage.getItem(LS_key_user)) {
    navigate('/')
  }

  if (!getLocalData() && !shouldVerifyDetails) {
    dispatch(setReduxState({
      name: 'shouldVerifyDetails',
      value: true
    }))
  }

  if (!shallShowTab(moduleName)) {
    return <Unauthorized />
  }
  return children

}

function App() {
  const appRouter = createBrowserRouter([
    {
      element: <ErrorBoundaryLayout />,
      children: [
        {
          path: "/",

          element: (
            <Suspense fallback={<h1>Loading..</h1>}>
              <LoginComponent />
            </Suspense>
          ),
        },
        {
          element: <ErrorBoundaryLayout />,
          children: [

            {
              path: "/dashboard",
              element:
                <Suspense fallback={<CheckAuth />}>
                  <DashboardComponent />
                </Suspense>,
              children: [
                {
                  path: "",
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <HomepageComponent />
                    </Suspense>
                  ),
                },
                {
                  path: votersRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_VOTER}>
                        <VotersComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: votersCallRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_VOTER}>
                        <VotersCallComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: nonVotersRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_NON_VOTER}>
                        <NonVotersComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: reportsRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_REPORTS}>
                        <ReportsComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: supportersRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_SUPPORTER}>
                        <SupportersComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: usersRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_USERS}>
                        <UsersComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: birthdaysRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_BIRTHDAYS}>
                        <BirthdaysComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                  children: [
                    {
                      path: ':personType',
                      element: (
                        <Suspense fallback={<CheckAuth />}>
                          <PrivateRoute moduleName={MODULE_BIRTHDAYS}>
                            <BirthdaysComponent />
                          </PrivateRoute>
                        </Suspense>
                      ),
                    }
                  ]
                },
                {
                  path: worksRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_WORKS}>
                        <WorksComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                  children: [
                    {
                      path: ':personType',
                      element: (
                        <Suspense fallback={<CheckAuth />}>
                          <PrivateRoute moduleName={MODULE_WORKS}>
                            <WorksComponent />
                          </PrivateRoute>
                        </Suspense>
                      ),
                    }
                  ]
                },
                {
                  path: electionPollsRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_ELECTION_POLLS}>
                        <ElectionPollsComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: appointmentsRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_APPOINTMENTS}>
                        <AppointmentsComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
                {
                  path: auditsRoute,
                  element: (
                    <Suspense fallback={<CheckAuth />}>
                      <PrivateRoute moduleName={MODULE_AUDIT}>
                        <AuditComponent />
                      </PrivateRoute>
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: "*",
              element:
                <Suspense fallback={<h1>Loading..</h1>}>
                  <PageNotFound404 />
                </Suspense>
            }

          ]
        }

      ]
    }
  ]);

  return (
    <div className="App h-full">
      <Provider store={store}>
      {/* <LanguageSelector /> */}
        <Helmet>
          <meta name="google" content="notranslate" />
        </Helmet>
        <RouterProvider router={appRouter} />
      </Provider>
    </div>
  );
}

const ErrorBoundaryLayout = () => (
  <ErrorBoundary FallbackComponent={CommonError}>
    <Outlet />
  </ErrorBoundary>
);

export default App;
